import { inject, Injectable } from '@angular/core'
import { combineLatestWith, map, Observable, of, Subject, switchMap, tap } from 'rxjs'
import { HomeState } from '../data-access/state/home.state'

import { Chantier } from '../../shared/models/chantier'
import { ChantierDto } from '../../shared/data-access/dto/chantier.dto'
import { ChantierHttp } from '../../shared/data-access/http/chantier.http'
import { ChantierMapper } from '../../shared/domain/mapper/chantier.mapper'
import { UserState } from '../../shared/data-access/state/user.state'
import { AccueilHttp } from '../data-access/http/accueil.http'
import { FilterModel, IServerSideDatasource, IServerSideGetRowsParams, SortModelItem } from 'ag-grid-community'
import { PageResponse } from '../../shared/models/page-response'
import { environment } from '../../../environments/environment'
import { AccueilMapper } from './mapper/accueil.mapper'
import { AccueilDto } from '../data-access/http/dto/accueil.dto'
import { formatDate } from '@angular/common'
import { ToastrService } from 'ngx-toastr'
import { EvenementDomainService } from '../../shared/domain/evenement.domain'
import { AccueilDetail } from '../../shared/models/accueil'
import { NgbModal } from '@ng-bootstrap/ng-bootstrap'

@Injectable({ providedIn: 'root' })
export class HomeDomain {
  private readonly accueilHttp = inject(AccueilHttp)
  private readonly chantierHttp = inject(ChantierHttp)
  private readonly userState = inject(UserState)
  private readonly homeState = inject(HomeState)
  private readonly toastr = inject(ToastrService)
  private readonly evenementDomainService = inject(EvenementDomainService)
  private readonly modalService = inject(NgbModal)
  readonly refreshRpcGrid$ = new Subject<void>()
  private lastCodeChantier: string | null = null

  // Selectors
  selectedChantier$: Observable<Chantier | null> = this.homeState.store
    .select(HomeState.getSelectedChantier)
    .pipe(map(ChantierMapper.dtoToModel))

  selectableUserChantiers$: Observable<Chantier[]> = this.userState.store.select(UserState.getUserChantiers).pipe(
    combineLatestWith(this.homeState.store.select(HomeState.getSelectedChantier)),
    map(([chantierDtos, selectedChantier]: [ChantierDto[], ChantierDto | null]) =>
      chantierDtos.filter((dto) => dto.code !== selectedChantier?.code),
    ),
    map(ChantierMapper.dtosToModels),
  )

  selectedAccueil$: Observable<AccueilDetail | null> = this.homeState.store.select(HomeState.getSelectedAccueil)

  isChantierEmpty$: Observable<boolean> = this.homeState.store.select(HomeState.getIsChantierEmpty)

  // Actions

  loadChantier(codeChantier: string): Observable<Chantier> {
    return this.userState.store.select(UserState.getUserChantiers).pipe(
      map((chantierDtos) => {
        return chantierDtos.find((c) => c.code === codeChantier)
      }),
      map(ChantierMapper.dtoToModel),
      tap((chantier) => {
        if (chantier) {
          this.homeState.store.update((state) => ({
            ...state,
            selectedChantier: chantier,
          }))
        }
      }),
    )
  }

  getAccueilDatasource(codeChantier: string): IServerSideDatasource {
    const ancienCodeChantier = this.lastCodeChantier
    this.lastCodeChantier = codeChantier
    let eventTracked = false

    return {
      getRows: (params: IServerSideGetRowsParams): void => {
        const { startRow } = params.request
        const pageSize = 10
        const pageNumber = startRow / pageSize
        const sortModel: SortModelItem[] = this.getAccueilSortModel(params.request.sortModel)
        const filterModel: FilterModel = params.request.filterModel
        const hasSearch = filterModel != null && Object.keys(filterModel).length > 0

        this.accueilHttp.findAccueilByChantier(codeChantier, pageSize, pageNumber, sortModel, filterModel).subscribe({
          next: (page: PageResponse<AccueilDto>) => {
            params.success({
              rowData: AccueilMapper.dtosToModel(page.content),
              rowCount: page.totalElements,
            })

            const checkChantierEmpty$: Observable<boolean> =
              page.totalElements > 0
                ? of(false)
                : hasSearch
                  ? this.accueilHttp
                      .findAccueilByChantier(codeChantier, 1, 0, [], {})
                      .pipe(map((unfilteredPage) => unfilteredPage.totalElements === 0))
                  : of(true)

            checkChantierEmpty$.subscribe((isEmpty) => {
              this.homeState.store.update((state) => ({
                ...state,
                isChantierEmpty: isEmpty,
              }))

              if (!eventTracked) {
                eventTracked = true
                const statutRegistre = isEmpty ? 'VIDE' : 'AVEC_DONNEES'
                this.evenementDomainService
                  .createSelectionChantierEvenement(ancienCodeChantier, codeChantier, statutRegistre)
                  .subscribe()
              }
            })
          },
        })
      },
    }
  }

  getAccueilDetail(id: number): Observable<AccueilDetail> {
    return this.accueilHttp.findById(id).pipe(
      map((dto) => AccueilMapper.dtoToDetailModel(dto)),
      tap((accueil) => {
        this.homeState.store.update((state) => ({
          ...state,
          selectedAccueil: accueil,
        }))
      }),
    )
  }

  patchDateSortie(id: number, dateSortie: string | Date | null, codeChantier: string): Observable<void> {
    const normalizeDate = (value: unknown): string | null => {
      if (value === null || value === undefined) return null
      if (value instanceof Date) return isNaN(value.getTime()) ? null : value.toISOString().split('T')[0]

      const strValue = String(value).trim()
      if (!strValue || strValue.toLowerCase() === 'vide' || strValue.toLowerCase() === 'null') return null

      if (strValue.includes('T')) {
        return strValue.split('T')[0]
      }
      if (/^\d{14}$/.test(strValue)) {
        const year = strValue.substring(0, 4)
        const month = strValue.substring(4, 6)
        const day = strValue.substring(6, 8)
        return `${year}-${month}-${day}`
      }

      return strValue
    }

    return this.accueilHttp.findById(id).pipe(
      switchMap((current) => {
        const formattedDateSortie = dateSortie ? formatDate(new Date(dateSortie), 'yyyyMMddHHmmss', 'fr') : null

        const payload = {
          dateEntree: current.dateEntree,
          dateSortie: formattedDateSortie,
          commentaire: current.commentaire,
          lienDriveTitreSejour: current.lienDriveTitreSejour,
        }

        const oldDate = normalizeDate(current.dateSortie)
        const newDate = normalizeDate(dateSortie)

        return this.accueilHttp.update(id, payload).pipe(
          switchMap(() => {
            if (oldDate !== newDate) {
              return this.evenementDomainService.createModificationTableauEvenement(
                id,
                codeChantier,
                'dateSortie',
                oldDate,
                newDate,
              )
            }
            return of(null)
          }),
        )
      }),
      map((): void => undefined),
      tap((): void => {
        this.refreshRpcGrid$.next()
        this.toastr.success('Date de sortie mise à jour !')
      }),
    )
  }

  patchAccueilModale(
    id: number,
    accueil: AccueilDetail,
    changes: {
      dateEntree?: string | null
      dateSortie?: string | null
      commentaire?: string | null
      lienDriveTitreSejour?: string | null
    },
  ): Observable<void> {
    const formatOrNull = (value: string | null): string | null =>
      value ? formatDate(new Date(value), 'yyyyMMddHHmmss', 'fr') : null

    const payload = {
      dateEntree: formatOrNull('dateEntree' in changes ? changes.dateEntree : accueil.dateEntree),
      dateSortie: formatOrNull('dateSortie' in changes ? changes.dateSortie : accueil.dateSortie),
      commentaire: 'commentaire' in changes ? changes.commentaire : accueil.commentaire,
      lienDriveTitreSejour:
        'lienDriveTitreSejour' in changes ? changes.lienDriveTitreSejour : accueil.lienDriveTitreSejour,
    }

    return this.accueilHttp.update(id, payload).pipe(
      map((): void => undefined),
      tap((): void => {
        this.refreshRpcGrid$.next()
        this.toastr.success('Fiche mise à jour !')
      }),
    )
  }

  deleteAccueil(id: number): Observable<void> {
    return this.accueilHttp.delete(id).pipe(
      tap(() => {
        this.refreshRpcGrid$.next()
        this.toastr.success('Le compagnon a été supprimé du registre !')
      }),
    )
  }

  redirectToAccueilPortailChantier(codeChantier: string): void {
    const url = `${environment.portailChantierUrl}/accueil-chantier?chantierCode=${encodeURIComponent(codeChantier)}`
    window.location.href = url
  }

  generatePdf(codeChantier: string): Observable<void> {
    return this.accueilHttp.generatePdf(codeChantier).pipe(
      tap((blob) => {
        if (!blob) return

        const pdfBlob = new Blob([blob], { type: 'application/pdf' })
        const url = window.URL.createObjectURL(pdfBlob)
        window.open(url, '_blank')
        setTimeout(() => window.URL.revokeObjectURL(url), 60000)
      }),
      map((): void => undefined),
    )
  }

  private getAccueilSortModel(sortModel: SortModelItem[]): SortModelItem[] {
    if (sortModel?.length) {
      return sortModel
    }
    return [{ sort: 'desc', colId: 'dateEntree' }]
  }
}
